<template>
  <section
    v-if="value && document"
    ref="panel"
    class="floating-document-window"
    :class="{ 'floating-document-window--interacting': interactionActive }"
    :style="panelStyle"
    role="dialog"
    aria-modal="false"
    :aria-labelledby="titleId"
    tabindex="-1"
    @keydown.esc.stop="close"
  >
    <header
      class="floating-document-window__header"
      title="Drag to reposition"
      @pointerdown="startDrag"
    >
      <span class="floating-document-window__grip" aria-hidden="true">
        <v-icon size="18">mdi-drag</v-icon>
      </span>
      <div class="floating-document-window__copy">
        <span class="floating-document-window__eyebrow">{{ eyebrow }}</span>
        <h2 :id="titleId">{{ documentTitle }}</h2>
      </div>
      <v-chip
        v-if="documentType"
        x-small
        outlined
        color="primary"
        class="floating-document-window__type"
      >
        {{ documentType }}
      </v-chip>
      <div class="floating-document-window__actions">
        <v-btn
          icon
          small
          :aria-label="`Reset ${windowLabel} window`"
          title="Reset window size and position"
          @click="resetWindow"
        >
          <v-icon size="18">mdi-fit-to-page-outline</v-icon>
        </v-btn>
        <v-btn
          icon
          small
          :aria-label="`Close ${windowLabel}`"
          :title="`Close ${windowLabel}`"
          @click="close"
        >
          <v-icon size="20">mdi-close</v-icon>
        </v-btn>
      </div>
    </header>

    <div class="floating-document-window__viewer">
      <DocumentViewer :key="documentKey" :item="document" />
    </div>

    <button
      type="button"
      class="floating-document-window__resize"
      :aria-label="`Resize ${windowLabel} window`"
      title="Drag or use arrow keys to resize"
      @pointerdown="startResize"
      @keydown="resizeWithKeyboard"
    />
  </section>
</template>

<script>
import DocumentViewer from '@/features/Task/components/DocumentViewer.vue';
import { documentIdentity, documentTypeLabel } from '@/features/Verification/utils/verificationContext';

const PANEL_EDGE_GAP = 8;
/**
 * Keep-out band at the foot of the workspace so a dragged or resized window can
 * never be parked under the viewer chrome: the 34 px ViewerStatusBar plus one
 * PANEL_EDGE_GAP. It was 74 while a second 32 px slice bar was docked above the
 * status bar; that bar was folded into the status bar, so the band shrank with
 * it. VerificationWizard keeps its own copy of this constant — keep them in step.
 */
const WORKSPACE_BOTTOM_CHROME = 42;
const DEFAULT_WIDTH = 720;
const DEFAULT_HEIGHT = 640;
const MIN_WIDTH = 420;
const MIN_HEIGHT = 320;
const KEYBOARD_RESIZE_STEP = 24;
const KEYBOARD_RESIZE_LARGE_STEP = 64;

function clamp(value, minimum, maximum) {
  return Math.min(Math.max(value, minimum), maximum);
}

export default {
  name: 'FloatingDocumentViewer',
  components: { DocumentViewer },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    document: {
      type: Object,
      default: null,
    },
    eyebrow: {
      type: String,
      default: 'Reference drawing',
    },
  },
  data() {
    return {
      windowRect: {
        left: PANEL_EDGE_GAP,
        top: PANEL_EDGE_GAP,
        width: DEFAULT_WIDTH,
        height: DEFAULT_HEIGHT,
      },
      windowInitialised: false,
      dragActive: false,
      dragPointerId: null,
      dragOffset: { x: 0, y: 0 },
      resizeActive: false,
      resizePointerId: null,
      resizeStart: null,
    };
  },
  computed: {
    titleId() {
      return `floating-document-title-${this._uid}`;
    },
    windowLabel() {
      return this.eyebrow.toLowerCase();
    },
    documentKey() {
      return documentIdentity(this.document) || this.document?.fileName || 'reference-document';
    },
    documentTitle() {
      const name = this.document?.documentNumber
        || this.document?.documentTitle
        || this.document?.fileName
        || 'Reference document';
      return [name, this.document?.revisionNumber].filter(Boolean).join(' · ');
    },
    documentType() {
      return this.document ? documentTypeLabel(this.document) : '';
    },
    interactionActive() {
      return this.dragActive || this.resizeActive;
    },
    panelStyle() {
      return {
        top: '0',
        left: '0',
        width: `${this.windowRect.width}px`,
        height: `${this.windowRect.height}px`,
        transform: `translate3d(${this.windowRect.left}px, ${this.windowRect.top}px, 0)`,
      };
    },
  },
  watch: {
    value(isOpen) {
      if (!isOpen) {
        this.stopInteractions();
        return;
      }
      this.$nextTick(() => {
        if (!this.windowInitialised) this.initialiseWindow();
        else this.constrainWindow();
        this.$refs.panel?.focus?.({ preventScroll: true });
      });
    },
    documentKey() {
      if (!this.value) return;
      this.$nextTick(this.constrainWindow);
    },
  },
  mounted() {
    window.addEventListener('resize', this.constrainWindow);
    if (this.value) {
      this.$nextTick(this.initialiseWindow);
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.constrainWindow);
    this.stopInteractions();
  },
  methods: {
    workspace() {
      return this.$refs.panel?.offsetParent || null;
    },
    initialiseWindow() {
      const workspace = this.workspace();
      if (!workspace) return;
      const availableWidth = Math.max(1, workspace.clientWidth - (PANEL_EDGE_GAP * 2));
      const availableHeight = Math.max(
        1,
        workspace.clientHeight - WORKSPACE_BOTTOM_CHROME - (PANEL_EDGE_GAP * 2),
      );
      this.windowRect = {
        left: PANEL_EDGE_GAP,
        top: PANEL_EDGE_GAP,
        width: Math.min(DEFAULT_WIDTH, availableWidth),
        height: Math.min(DEFAULT_HEIGHT, availableHeight),
      };
      this.windowInitialised = true;
    },
    constrainWindow() {
      if (!this.windowInitialised) return;
      this.$nextTick(() => {
        const workspace = this.workspace();
        if (!workspace) return;
        const maxWidth = Math.max(1, workspace.clientWidth - (PANEL_EDGE_GAP * 2));
        const maxHeight = Math.max(
          1,
          workspace.clientHeight - WORKSPACE_BOTTOM_CHROME - (PANEL_EDGE_GAP * 2),
        );
        const width = clamp(
          this.windowRect.width,
          Math.min(MIN_WIDTH, maxWidth),
          maxWidth,
        );
        const height = clamp(
          this.windowRect.height,
          Math.min(MIN_HEIGHT, maxHeight),
          maxHeight,
        );
        this.windowRect = {
          left: clamp(
            this.windowRect.left,
            PANEL_EDGE_GAP,
            Math.max(PANEL_EDGE_GAP, workspace.clientWidth - width - PANEL_EDGE_GAP),
          ),
          top: clamp(
            this.windowRect.top,
            PANEL_EDGE_GAP,
            Math.max(
              PANEL_EDGE_GAP,
              workspace.clientHeight - height - WORKSPACE_BOTTOM_CHROME,
            ),
          ),
          width,
          height,
        };
      });
    },
    startDrag(event) {
      if (
        event.button !== 0
        || event.target?.closest?.('button, input, textarea, select, a')
      ) return;
      const panel = this.$refs.panel;
      const workspace = this.workspace();
      if (!panel || !workspace) return;

      const panelRect = panel.getBoundingClientRect();
      this.windowRect = {
        ...this.windowRect,
        left: panelRect.left - workspace.getBoundingClientRect().left,
        top: panelRect.top - workspace.getBoundingClientRect().top,
      };
      this.dragOffset = {
        x: event.clientX - panelRect.left,
        y: event.clientY - panelRect.top,
      };
      this.dragPointerId = event.pointerId;
      this.dragActive = true;
      event.currentTarget?.setPointerCapture?.(event.pointerId);
      event.preventDefault();

      window.addEventListener('pointermove', this.onDragMove, { passive: false });
      window.addEventListener('pointerup', this.stopDrag);
      window.addEventListener('pointercancel', this.stopDrag);
    },
    onDragMove(event) {
      if (!this.dragActive || event.pointerId !== this.dragPointerId) return;
      const workspace = this.workspace();
      if (!workspace) {
        this.stopDrag();
        return;
      }
      const workspaceRect = workspace.getBoundingClientRect();
      this.windowRect = {
        ...this.windowRect,
        left: clamp(
          event.clientX - workspaceRect.left - this.dragOffset.x,
          PANEL_EDGE_GAP,
          Math.max(
            PANEL_EDGE_GAP,
            workspace.clientWidth - this.windowRect.width - PANEL_EDGE_GAP,
          ),
        ),
        top: clamp(
          event.clientY - workspaceRect.top - this.dragOffset.y,
          PANEL_EDGE_GAP,
          Math.max(
            PANEL_EDGE_GAP,
            workspace.clientHeight - this.windowRect.height - WORKSPACE_BOTTOM_CHROME,
          ),
        ),
      };
      event.preventDefault();
    },
    stopDrag(event = null) {
      if (event && event.pointerId !== this.dragPointerId) return;
      this.dragActive = false;
      this.dragPointerId = null;
      window.removeEventListener('pointermove', this.onDragMove);
      window.removeEventListener('pointerup', this.stopDrag);
      window.removeEventListener('pointercancel', this.stopDrag);
    },
    startResize(event) {
      if (event.button !== 0) return;
      this.resizeActive = true;
      this.resizePointerId = event.pointerId;
      this.resizeStart = {
        x: event.clientX,
        y: event.clientY,
        width: this.windowRect.width,
        height: this.windowRect.height,
      };
      event.currentTarget?.setPointerCapture?.(event.pointerId);
      event.preventDefault();
      event.stopPropagation();

      window.addEventListener('pointermove', this.onResizeMove, { passive: false });
      window.addEventListener('pointerup', this.stopResize);
      window.addEventListener('pointercancel', this.stopResize);
    },
    onResizeMove(event) {
      if (!this.resizeActive || event.pointerId !== this.resizePointerId) return;
      this.resizeBy(
        this.resizeStart.width + event.clientX - this.resizeStart.x,
        this.resizeStart.height + event.clientY - this.resizeStart.y,
      );
      event.preventDefault();
    },
    resizeBy(width, height) {
      const workspace = this.workspace();
      if (!workspace) return;
      const maxWidth = Math.max(
        1,
        workspace.clientWidth - this.windowRect.left - PANEL_EDGE_GAP,
      );
      const maxHeight = Math.max(
        1,
        workspace.clientHeight
          - this.windowRect.top
          - WORKSPACE_BOTTOM_CHROME,
      );
      this.windowRect = {
        ...this.windowRect,
        width: clamp(width, Math.min(MIN_WIDTH, maxWidth), maxWidth),
        height: clamp(height, Math.min(MIN_HEIGHT, maxHeight), maxHeight),
      };
    },
    stopResize(event = null) {
      if (event && event.pointerId !== this.resizePointerId) return;
      this.resizeActive = false;
      this.resizePointerId = null;
      this.resizeStart = null;
      window.removeEventListener('pointermove', this.onResizeMove);
      window.removeEventListener('pointerup', this.stopResize);
      window.removeEventListener('pointercancel', this.stopResize);
    },
    resizeWithKeyboard(event) {
      if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
        return;
      }
      const step = event.shiftKey ? KEYBOARD_RESIZE_LARGE_STEP : KEYBOARD_RESIZE_STEP;
      const widthDelta = event.key === 'ArrowLeft'
        ? -step
        : event.key === 'ArrowRight'
          ? step
          : 0;
      const heightDelta = event.key === 'ArrowUp'
        ? -step
        : event.key === 'ArrowDown'
          ? step
          : 0;
      this.resizeBy(
        this.windowRect.width + widthDelta,
        this.windowRect.height + heightDelta,
      );
      event.preventDefault();
    },
    stopInteractions() {
      this.stopDrag();
      this.stopResize();
    },
    resetWindow() {
      this.stopInteractions();
      this.initialiseWindow();
    },
    close() {
      this.stopInteractions();
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../styles/verification-tokens';

.floating-document-window {
  position: absolute;
  z-index: 9;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  border-radius: 8px;
  box-shadow: $vf-shadow-2;
  color: $vf-ink;
  outline: none;

  &:focus-visible {
    outline: 2px solid $vf-primary;
    outline-offset: 2px;
  }
}

.floating-document-window--interacting {
  user-select: none;

  ::v-deep iframe {
    pointer-events: none;
  }
}

.floating-document-window__header {
  min-height: 54px;
  padding: 8px 8px 8px 10px;
  border-bottom: 1px solid $vf-line;
  background: $vf-panel;
  display: flex;
  align-items: center;
  gap: 9px;
  cursor: grab;
  touch-action: none;
  user-select: none;

  &:active {
    cursor: grabbing;
  }
}

.floating-document-window__grip {
  flex: 0 0 auto;
  color: $vf-ink-3;
  display: grid;
  place-items: center;
}

.floating-document-window__copy {
  min-width: 0;
  flex: 1;

  h2 {
    margin: 1px 0 0;
    overflow: hidden;
    color: $vf-ink;
    font-family: $vf-font-body;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.25;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.floating-document-window__eyebrow {
  display: block;
  color: $vf-ink-3;
  font-family: $vf-font-mono;
  font-size: 9px;
  letter-spacing: 0.1em;
  line-height: 1.2;
  text-transform: uppercase;
}

.floating-document-window__type {
  flex: 0 0 auto;
  max-width: 100px;

  ::v-deep .v-chip__content {
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.floating-document-window__actions {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
}

.floating-document-window__viewer {
  min-width: 0;
  min-height: 0;
  flex: 1;
  background: $vf-bg;
}

.floating-document-window__resize {
  position: absolute;
  z-index: 2;
  right: 0;
  bottom: 0;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 0 0 9px 0;
  background:
    linear-gradient(
      135deg,
      transparent 0 50%,
      rgba($vf-primary, 0.42) 50% 55%,
      transparent 55% 66%,
      rgba($vf-primary, 0.62) 66% 71%,
      transparent 71%
    );
  cursor: nwse-resize;
  touch-action: none;

  &:focus-visible {
    outline: 2px solid $vf-primary;
    outline-offset: -3px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .floating-document-window {
    transition: none;
  }
}
</style>
